import React from 'react';

import {ApolloClient, ApolloLink, CombinedGraphQLErrors, InMemoryCache, gql} from '@apollo/client';

declare module '@apollo/client' {
  namespace ApolloClient {
    namespace DeclareDefaultOptions {
      interface WatchQuery {
        errorPolicy?: 'all' | 'ignore';
      }
      interface Query {
        errorPolicy?: 'all';
      }
    }
  }
}
import {ApolloProvider} from '@apollo/client/react';
import {SetContextLink} from '@apollo/client/link/context';
import {ErrorLink} from '@apollo/client/link/error';
import {RemoveTypenameFromVariablesLink} from '@apollo/client/link/remove-typename';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import {from as observableFrom} from 'rxjs';
import {filter, mergeMap} from 'rxjs/operators';

import './App.css';
import 'leaflet-gpx';
import 'react-toastify/dist/ReactToastify.css';
import './assets/css/sidebar.css';
import './assets/css/admin-layout.css';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {ToastContainer} from 'react-toastify';

import MainRouter from './routes/Main';
import LocalStorageService from './services/LocalStorageService';
import {ThemeProvider, createTheme} from '@mui/material';

const brandPink = '#ff0066';

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: brandPink,
      light: '#ff3385',
      dark: '#cc0052',
      contrastText: '#ffffff'
    },
    secondary: {
      main: '#5b6470',
      contrastText: '#ffffff'
    },
    background: {
      default: '#f4f5f7',
      paper: '#ffffff'
    },
    text: {
      primary: '#1f2933',
      secondary: '#6b7280'
    },
    divider: '#e6e8ec'
  },
  shape: {
    borderRadius: 10
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.4px',
      fontSize: 22,
      '@media (min-width:600px)': {fontSize: 26},
      '@media (min-width:900px)': {fontSize: 28}
    },
    h5: {
      fontWeight: 700,
      letterSpacing: '-0.3px',
      fontSize: 18,
      '@media (min-width:600px)': {fontSize: 22}
    },
    h6: {fontSize: 16, fontWeight: 600, '@media (min-width:600px)': {fontSize: 17}}
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {overflowX: 'hidden'},
        body: {
          backgroundColor: '#f4f5f7',
          color: '#1f2933',
          overflowX: 'hidden'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: 'none',
          border: '1px solid #e6e8ec'
        }
      }
    },
    MuiCard: {
      defaultProps: {elevation: 0},
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid #e6e8ec',
          borderRadius: 10
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 16,
          '@media (max-width:599px)': {
            padding: 14
          },
          '&:last-child': {
            paddingBottom: 16
          }
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          height: 52,
          '@media (max-width:899px)': {height: 'auto', minHeight: 52}
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8
        },
        containedPrimary: {
          boxShadow: 'none',
          '&:hover': {boxShadow: 'none'}
        },
        outlined: {
          borderColor: '#e6e8ec',
          color: '#1f2933',
          '&:hover': {
            borderColor: brandPink,
            backgroundColor: 'rgba(255, 0, 102, 0.04)',
            color: brandPink
          }
        }
      }
    },
    MuiLink: {
      defaultProps: {
        underline: 'none'
      },
      styleOverrides: {
        root: {
          color: brandPink,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline'
          }
        }
      }
    }
  }
});

const GET_TOKEN_QUERY = gql`
  query GetToken($refresh: String!) {
    getToken(refresh: $refresh)
  }
`;

let pendingRefresh: Promise<string | null> | null = null;

const refreshAccessToken = (client: ApolloClient): Promise<string | null> => {
  if (pendingRefresh) {
    return pendingRefresh;
  }
  pendingRefresh = client
    .query({
      query: GET_TOKEN_QUERY,
      variables: {refresh: LocalStorageService.getRefreshToken()},
      fetchPolicy: 'no-cache'
    })
    .then(({data}) => {
      const token: string | null = (data as {getToken?: string} | undefined)?.getToken ?? null;
      if (token) {
        LocalStorageService.setAccessToken(token);
      }
      return token;
    })
    .catch(() => null)
    .finally(() => {
      pendingRefresh = null;
    });
  return pendingRefresh;
};

const errorLink = new ErrorLink(({error, operation, forward}) => {
  if (!CombinedGraphQLErrors.is(error)) return;
  const isExpiredSession = error.errors.some(
    (e) => e.extensions?.code === 'UNAUTHENTICATED' && e.message === 'Unauthenticated!'
  );
  if (!isExpiredSession) return;

  return observableFrom(refreshAccessToken(operation.client)).pipe(
    filter((value): value is string => Boolean(value)),
    mergeMap((accessToken) => {
      const oldHeaders = operation.getContext().headers;
      operation.setContext({
        headers: {
          ...oldHeaders,
          authorization: `Bearer ${accessToken}`
        }
      });
      return forward(operation);
    })
  );
});

const httpLink = new UploadHttpLink({
  uri: import.meta.env.VITE_GRAPHQL
});

const authLink = new SetContextLink((prevContext) => {
  const accessToken = LocalStorageService.getAccessToken();
  return {
    headers: {
      ...prevContext.headers,
      authorization: accessToken ? `Bearer ${accessToken}` : ''
    }
  };
});

const removeTypenameLink = new RemoveTypenameFromVariablesLink();

const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, removeTypenameLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore'
    },
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all'
    }
  }
});

const App = () => (
  <ApolloProvider client={apolloClient}>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ThemeProvider theme={defaultTheme}>
        <MainRouter />
        <ToastContainer position="top-right" autoClose={3000} theme="light" />
      </ThemeProvider>
    </LocalizationProvider>
  </ApolloProvider>
);

export default App;
